import { isWithFilterEntries, encodeScalarIfDateOrTime, encodeArrayField, parseBraceOrCsv, isWithItemTitle, isWithValueTitle, isWithMultipleValues, setMainValue, encodeValues } from '../core/EncodeValues.js';
import { Widget } from '../core/Widget.js';
import { FilterFieldTypes, FilterOperators } from './FilterFieldMap.js';

// search-core/FilterDomManager.ts
// 2026-04-29: для некоторых виджетов, которые возвращают несколько значений,
// в разметке нужен `data-array=true`, иначе URL/restore могут обрабатывать их как scalar.
// Пример: `listbox` и другие составные фильтры.
/** Логический вид фильтра */
var FilterUiType;
(function (FilterUiType) {
    /** Строка фильтра в заголовке таблицы. */
    FilterUiType["row"] = "row";
    /** Фильтр в виде формы, специальная разметка полей */
    FilterUiType["form"] = "form";
    /** Иной/кастомный контейнер. */
    FilterUiType["custom"] = "custom";
})(FilterUiType || (FilterUiType = {}));
/** Чтение фильтра из DOM */
class FilterDomManager {
    /**
     * @param host Корневой хост фильтра (уже известен на уровне виджета).
     */
    constructor(host, options) {
        const op = options ?? {};
        if (!(host instanceof HTMLElement)) {
            throw new Error('[FilterDomManager] host must be an HTMLElement');
        }
        this.host = host;
        this.uiType = this._detectUiType(host);
        // если в опциях передан ключ владельца → формируем префикс
        if (op.ownerDataKey) {
            this.dataKeyPrefix = `_tfc_${op.ownerDataKey}__`;
        }
        else if (this.uiType != 'custom') {
            this.dataKeyPrefix = `_tfc_`;
        }
        else
            this.dataKeyPrefix = op.prefix ?? '';
        // Определение viewKey
        this.viewKey = this._resolveViewKey(host, op.viewKey);
    }
    /**
     * Определение типа расположения фильтра:
     * - внутри строки заголовка таблицы (tr.im-table-filter)
     * - внутри панели (aside/.modal-aside)
     * - иной/кастомный контейнер
     */
    _detectUiType(el) {
        // Ветка: фильтр — строка в THEAD (как в legacy .im-table-filter)
        const row = el.closest('.im-table-filter');
        if (row)
            return FilterUiType.row;
        // Ветка: фильтр — боковая/модальная панель
        const aside = el.closest('.im-filter-form') || el.querySelector('.im-filter-form');
        if (aside)
            return FilterUiType.form;
        // Иное размещение — считаем кастомным (на будущее расширим)
        return FilterUiType.custom;
    }
    /** Резолв ключа view с учётом приоритетов. */
    _resolveViewKey(host, optViewKey) {
        // 1) Явно передан в опциях
        if (optViewKey)
            return optViewKey;
        // 2) Сам хост — это view или просто имеет data-key
        const hostDataKey = host.getAttribute('data-key');
        if (host.classList.contains('im-view') && hostDataKey)
            return hostDataKey;
        if (hostDataKey)
            return hostDataKey;
        // 3) Ближайший .im-view вверх по дереву
        const ownerView = host.closest('.im-view');
        const ownerKey = ownerView?.getAttribute('data-key');
        if (ownerKey)
            return ownerKey;
        // Ничего не найдено — оставляем пусто
        return '';
    }
    /**
     * Собирает карту полей фильтра из DOM-хоста.
     * Заполняются: dataKey, viewKey, key.
     * Остальные свойства — дефолтами/заглушками.
     */
    collectFieldMap() {
        const result = {};
        const nodes = this.host.querySelectorAll("[data-key]");
        nodes.forEach((el) => {
            const rawDataKey = el.getAttribute("data-key");
            if (!rawDataKey)
                return;
            // Логический ключ поля. Можно переопределить через data-fkey="..."
            const key = el.dataset.fkey || this._extractKey(rawDataKey);
            if (!key)
                return;
            // Если уже встретили такой ключ — не перезаписываем
            if (result[key])
                return;
            const fieldViewKeyAttr = el.dataset.viewkey || "";
            const viewKey = fieldViewKeyAttr ||
                (this.viewKey && this.viewKey ? this.viewKey : undefined);
            // Значение по умолчанию (если задано через data-fdefault):
            // - используется при decode (если параметра нет в URL)
            // - и при "сбросе" поля по умолчанию из параметров
            let fieldDefault = el.dataset.fdefault;
            let fieldType = this._parseFieldType(el.dataset.ftype);
            // Пресет для чекбокса
            if (!fieldDefault) {
                if (el instanceof HTMLInputElement && el.type === "checkbox") {
                    // Если простой чек бокс, то false это значит что фильтр не активен
                    fieldType = FilterFieldTypes.boolean;
                    fieldDefault = false;
                }
            }
            // Определяем операторы
            // data-fop: список операторов через запятую (строки из enum FilterOperators).
            // Пример: data-fop="eq,between,in".
            // Если не задан — операторы выводятся эвристически по типу контрола (textarea/textbox → text).
            let ops;
            if (el.dataset.fop) {
                ops = this._parseOperators(el.dataset.fop);
            }
            else if (el instanceof HTMLTextAreaElement) {
                ops = [FilterOperators.text];
            }
            else if (el instanceof HTMLInputElement) {
                // textbox = type отсутствует (по умолчанию text) или явно text/search
                const t = (el.getAttribute("type") ?? "text").toLowerCase();
                ops = (t === "text" || t === "search") ? [FilterOperators.text] : [];
            }
            else {
                ops = [];
            }
            // data-fvis: видимость значения в UI (чипсы/бейдж).
            // Содержит символы: "c" = показывать в chips, "b" = учитывать в badge.
            // Если атрибут отсутствует — считаем "показываем везде" (showInChips/showInBadge остаются undefined).
            // Пример: data-fvis="c" (только chips), data-fvis="cb" (chips + badge).
            const fvis = el.dataset.fvis;
            let showInChips;
            let showInBadge;
            // если атрибута нет — значит "показываем всё"
            if (fvis !== undefined) {
                showInChips = fvis.includes("c");
                showInBadge = fvis.includes("b");
            }
            // --------- Считываем режимы работы с url
            // - data-urlcommit: как фиксировать URL (push/replace/redirect)
            const urlCommitMode = this._parseUrlCommitMode(el.dataset.urlcommit);
            // - data-urlloc: где хранить значение (query или path)
            const urlLocation = this._parseUrlLocation(el.dataset.urlloc);
            // - data-urlkey: альтернативный ключ параметра в URL (если отличается от schema.key)
            const urlKey = el.dataset.urlkey;
            // - data-urlpath: regex для чтения/замены в pathname (group 1 = value)
            const urlPathRegex = el.dataset.urlpath;
            // - data-presetkey: ключ получения пресета для доп обработки фильтра
            const presetKey = el.dataset.presetkey;
            // - data-chipsgroup: ключ получения пресета для доп обработки фильтра
            const chipsGroup = el.dataset.chipsgroup;
            // - data-array: ожидается что значение это массив
            const array = parseBoolean(el.dataset.array);
            // - data-chipsarray: трактовать чипсы как массив, для каждого элемента будет создаваться свой чипс
            const chipsArray = parseBoolean(el.dataset.chipsarray);
            // - data-chipsarraysummary - Режим подписи summary-чипса для массивов (если chipsArray !== true)
            const chipsArraySummary = this._parseChipsArraySummary(el.dataset.chipsarraysummary);
            // - data-chipsvalueonly - Показывать в чипсе только значение (без "label -")
            const chipsValueOnly = parseBoolean(el.dataset.chipsvalueonly) || chipsArray;
            // - data-encode - алгоритм кодирования значений
            const encodeMethod = this._parseEncMethod(el.dataset.encode);
            // - data-agg: поле агрегации, его нужно перегружать при изменении других фильтров
            const isAgg = parseBoolean(el.dataset.agg);
            // - data-noagg: изменения этого поля не должны влиять на агрегации
            const noAggEffect = parseBoolean(el.dataset.noagg);
            let min;
            let max;
            // min/max: читаем всегда (и из стандартных атрибутов, и из data-*)
            {
                const minRaw = el.getAttribute("min") ?? el.getAttribute("data-min");
                const maxRaw = el.getAttribute("max") ?? el.getAttribute("data-max");
                const minNum = minRaw != null && minRaw !== "" ? Number(minRaw) : NaN;
                const maxNum = maxRaw != null && maxRaw !== "" ? Number(maxRaw) : NaN;
                if (isFinite(minNum))
                    min = minNum;
                if (isFinite(maxNum))
                    max = maxNum;
            }
            // Если тип не задан, но есть ограничения — считаем числом
            if (fieldType === FilterFieldTypes.unknown && (min != null || max != null)) {
                const scaleRaw = el.dataset.scale;
                const scaleNum = scaleRaw != null && scaleRaw !== "" ? Number(scaleRaw) : NaN;
                // scale>0 => дробное, иначе целое
                fieldType = isFinite(scaleNum) && scaleNum > 0
                    ? FilterFieldTypes.number
                    : FilterFieldTypes.int;
            }
            result[key] = {
                // IFilterFieldSchema
                key,
                type: fieldType,
                isArray: array,
                label: this._resolveFieldLabel(el) || key,
                operators: ops,
                // расширение FilterFieldMap
                dataKey: rawDataKey,
                viewKey,
                default: fieldDefault,
                sourceElement: el,
                // Работа с чипсами и бейджем
                showInBadge: showInBadge,
                showInChips: showInChips,
                chipsGroup,
                chipsArray,
                chipsArraySummary,
                chipsValueOnly,
                // url options (для SearchUrlCodec)
                urlCommitMode,
                urlLocation,
                urlKey,
                urlPathRegex,
                presetKey,
                encMethod: encodeMethod,
                isAgg,
                noAggEffect,
                min,
                max
            };
        });
        const widgetNodes = this.host.querySelectorAll("[data-widget]");
        widgetNodes.forEach((node) => {
            const widgets = Widget.allOn(node);
            for (const w of widgets) {
                if (!isWithFilterEntries(w))
                    continue;
                const entries = w.getFilterEntries() ?? [];
                for (const entry of entries) {
                    const schema = entry?.schema;
                    if (!schema?.key || result[schema.key])
                        continue;
                    const dataKey = node.getAttribute("data-key") || schema.key;
                    // 2026-04-29: для составных виджетов сохраняем реальный узел-источник,
                    // чтобы дальше не искать его повторно только по data-key.
                    result[schema.key] = {
                        ...schema,
                        label: schema.label || schema.key,
                        dataKey,
                        viewKey: node.dataset.viewkey || (this.viewKey && this.viewKey ? this.viewKey : undefined),
                        sourceElement: node
                    };
                }
            }
        });
        return result;
    }
    _parseFieldType(raw) {
        const v = (raw ?? "").trim().toLowerCase();
        switch (v) {
            case "int": return FilterFieldTypes.int;
            case "number": return FilterFieldTypes.number;
            case "text": return FilterFieldTypes.text;
            case "boolean": return FilterFieldTypes.boolean;
            case "enum": return FilterFieldTypes.enum;
            case "date": return FilterFieldTypes.date;
            case "daterange": return FilterFieldTypes.dateRange;
            case "numberrange": return FilterFieldTypes.numberRange;
            case "guid": return FilterFieldTypes.guid;
            default: return FilterFieldTypes.unknown;
        }
    }
    _parseChipsArraySummary(raw) {
        if (!raw)
            return undefined;
        const v = raw.toLowerCase();
        if (v === "join" || v === "first" || v === "count")
            return v;
        return undefined;
    }
    /**
     * Разобрать метод кодирования значения для URL из data-encode.
     * Сейчас поддерживается только "guid64".
     */
    _parseEncMethod(raw) {
        const v = (raw ?? "").trim().toLowerCase();
        return v === "guid64" ? "guid64" : undefined;
    }
    /** Прочитать режим фиксации URL из dataset. */
    _parseUrlCommitMode(raw) {
        if (!raw)
            return undefined;
        const v = raw.trim().toLowerCase();
        if (v === "push" || v === "replace" || v === "redirect")
            return v;
        return undefined;
    }
    /** Прочитать расположение значения в URL из dataset. */
    _parseUrlLocation(raw) {
        if (!raw)
            return undefined;
        const v = raw.trim().toLowerCase();
        if (v === "query" || v === "path")
            return v;
        return undefined;
    }
    /** Парсинг операторов фильтра */
    _parseOperators(raw) {
        if (!raw)
            return [];
        const allowed = new Set(Object.values(FilterOperators));
        const out = [];
        const seen = new Set();
        for (const part of raw.split(",")) {
            const token = part.trim();
            if (!token)
                continue;
            // строго: значение должно совпасть с enum-строкой
            if (!allowed.has(token))
                continue;
            const op = token;
            if (seen.has(op))
                continue;
            seen.add(op);
            out.push(op);
        }
        return out;
    }
    /**
     * Утилита извлечения ключа без префикса
     *
     * Возвращает "чистый" ключ без `_tfc_*__` префикса.
     * При наличии переданного префикса - несовпадающие dataKey игнорируются.
     */
    _extractKey(dataKey) {
        let key = dataKey.trim();
        if (!key)
            return "";
        // 1) Если известен конкретный префикс 
        if (this.dataKeyPrefix) {
            // Если ключ не совпадает с префиксом, то игнорируем его
            if (!key.startsWith(this.dataKeyPrefix))
                return null;
        }
        // 2) Общий случай `_tfc_<owner>__*`
        const m = key.match(/^_tfc_[^_]+__(.+)$/);
        if (m)
            return m[1];
        // key может содержать подчёркивания → первое двойное подчёркивание
        const idx = key.indexOf('__');
        if (idx === -1)
            // Без префикса — возвращаем как есть
            return null;
        // всё после '__'
        return key.slice(idx + 2);
    }
    /** Определение label:
     * 1) aria, если есть
     * 2) если uiType='form' и поле в разметке таблицы: берём текст из 1-го <td> в той же строке <tr>, где поле во 2-ом <td>
     * 3) aria-label как fallback
     */
    _resolveFieldLabel(inputEl) {
        let aria = inputEl.dataset.ftitle;
        if (aria)
            return aria;
        aria = inputEl.getAttribute("aria-label") || "";
        if (this.uiType === FilterUiType.form) {
            const td = inputEl.closest("td");
            const tr = td?.closest("tr");
            if (td && tr) {
                // берём только прямых детей-td
                const tds = Array.from(tr.children).filter((c) => c instanceof HTMLTableCellElement);
                if (tds.length === 2) {
                    const [labelTd, valueTd] = tds;
                    if (valueTd.contains(inputEl)) {
                        return this._cleanLabel(labelTd.textContent || aria);
                    }
                }
            }
        }
        return aria;
    }
    /** Превратить сырое значение фильтра в строку для FilterValue. */
    _encodeFilterValue(value) {
        // 2026-04-29: нормализуем составные значения в тот же строковый формат,
        // который ожидает остальная фильтровая цепочка.
        if (value === undefined)
            return undefined;
        if (value === null)
            return "dbnull";
        if (Array.isArray(value)) {
            const mapped = value.map(encodeScalarIfDateOrTime);
            return encodeArrayField(mapped);
        }
        const scalar = encodeScalarIfDateOrTime(value);
        return String(scalar);
    }
    /** Подчистка текста метки: убрать хвостовые двоеточия и лишние пробелы */
    _cleanLabel(text) {
        return text.replace(/\s+/g, " ").replace(/[:：]\s*$/u, "").trim();
    }
    /** Проверка что поле имеет значение */
    _isNotNull(v, fm) {
        if (v === undefined || v === null)
            return false;
        if (fm) {
            // Отсекаем дефолтные значения, считаем их не активным значением фильтра
            if (v == fm.default)
                return false;
        }
        if (typeof v === "string") {
            const s = v.trim();
            if (s === "")
                return false; // пустая строка
            if (s.toLowerCase() === "dbnull")
                return false; // маркер null
            return true;
        }
        // boolean/number/другое — считаем осмысленным (false и 0 допустимы)
        return true;
    }
    /** Найти «нативный» контрол внутри узла с data-key (или сам элемент) */
    _findNativeControl(el) {
        if (el instanceof HTMLInputElement || el instanceof HTMLSelectElement || el instanceof HTMLTextAreaElement) {
            return el;
        }
        return (el.querySelector('input') ||
            el.querySelector('select') ||
            el.querySelector('textarea') ||
            null);
    }
    /** Получить "valueTitle" для отображения (минимум как в старом imtable) */
    _resolveValueTitle(el, valueStr, fm) {
        if (!valueStr)
            return "";
        if (!el)
            return valueStr;
        // Виджеты на элементе сами могут вернуть valueTitle
        const widgets = Widget.allOn(el);
        // ARRAY: собираем title по каждому элементу массива (без split(','))
        if (fm?.isArray) {
            const values = parseBraceOrCsv(valueStr ?? "")
                .map(x => String(x).trim())
                .filter(Boolean);
            if (values.length === 0)
                return "";
            for (const w of widgets) {
                if (!isWithItemTitle(w))
                    continue;
                const titles = values.map(v => w.getItemTitle(v) ?? v);
                return titles.join(", ");
            }
            // нет WithItemTitle → падаем дальше на select[multiple] и прочие DOM fallback
        }
        for (const w of widgets) {
            if (isWithValueTitle(w)) {
                const title = w.getValueTitle();
                if (title)
                    return title;
            }
        }
        // если data-key на контейнере — смотрим реальный контрол внутри
        const ctrl = this._findNativeControl(el) ?? el;
        // select[multiple]: объединяем тексты выбранных опций
        if (ctrl instanceof HTMLSelectElement && ctrl.multiple) {
            const titles = Array.from(ctrl.selectedOptions).map(o => (o.textContent ?? '').trim()).filter(Boolean);
            if (titles.length)
                return titles.join(', ');
            return valueStr;
        }
        // select (single): текст выбранной опции
        if (ctrl instanceof HTMLSelectElement) {
            const opt = ctrl.selectedOptions[0];
            if (opt)
                return (opt.textContent ?? '').trim() || valueStr;
            return valueStr;
        }
        // combobox (im-combo): используем data-title
        if (el.classList.contains('im-combo')) {
            const title = el.getAttribute('data-title');
            if (title && title.trim() !== '')
                return title.trim();
        }
        // checkbox → используем label поля, а не value ("on")
        if (ctrl instanceof HTMLInputElement && ctrl.type === "checkbox" && valueStr === 'true') {
            // Возврат чекбокса в виде специальной метки
            return '{on}';
        }
        // input/textarea → отдаём текущее текстовое содержимое
        if (ctrl instanceof HTMLInputElement || ctrl instanceof HTMLTextAreaElement) {
            // Для checkbox/radio в минимальной версии показываем само value из DOM
            // (при желании можно локализовать true/false позже)
            return ctrl.value?.trim() ?? valueStr;
        }
        // fallback → само значение
        return valueStr;
    }
    /** Применить значение к полю с учетом multi-value виджетов. */
    _setFieldMapValue(fm, value) {
        // 2026-04-29: сначала работаем с реальным узлом из fieldMap,
        // fallback по data-key нужен только для старых схем.
        const node = fm.sourceElement
            ?? this.host.querySelector(`[data-key="${fm.dataKey}"]`);
        if (!node)
            return false;
        const widgets = Widget.allOn(node);
        for (const w of widgets) {
            if (isWithMultipleValues(w)) {
                if (w.setValues({ [fm.key]: value })) {
                    return true;
                }
            }
        }
        return setMainValue(node, value);
    }
    /** Сброс фильтра по ключу (поддерживает и "key", и "dataKey") */
    clearKey(keyOrDataKey) {
        if (!keyOrDataKey)
            return;
        // 1) Собираем карту полей
        const fieldMap = this.collectFieldMap(); // Record<key, FilterFieldMap>
        // 2) Находим подходящие записи:
        //    — точное совпадение по dataKey
        //    — или точное совпадение по key
        const targets = [];
        // приоритет: dataKey → key
        for (const fm of Object.values(fieldMap)) {
            if (fm.dataKey === keyOrDataKey) {
                targets.push(fm);
            }
        }
        if (targets.length === 0) {
            for (const fm of Object.values(fieldMap)) {
                if (fm.key === keyOrDataKey) {
                    targets.push(fm);
                }
            }
        }
        // 2026-04-29: очистка идет через единый set-метод, чтобы составные виджеты
        // сбрасывались по своему контракту, а не только через data-key.
        for (const fm of targets) {
            this._setFieldMapValue(fm, null);
        }
    }
    /**
     * Полный сброс значений фильтра в пределах текущего host.
     * Берём ВСЕ [data-key], но очищаем только те, которые входят в collectFieldMap()
     * (чтобы не трогать чужие элементы/префиксы).
     */
    clearAll() {
        // 1) Собираем карту разрешённых полей
        const fieldMap = this.collectFieldMap();
        const allowed = new Set(Object.values(fieldMap).map((f) => f.dataKey));
        // 2026-04-29: чистим только поля из fieldMap, чтобы не трогать чужие узлы
        // и при этом корректно сбрасывать составные виджеты через их контракт.
        for (const fm of Object.values(fieldMap)) {
            if (!allowed.has(fm.dataKey) && !allowed.has(fm.key))
                continue;
            this._setFieldMapValue(fm, null);
        }
        // Сбрасываем подстветку
        this.updateLabelHighlight([]);
    }
    /** Подсветка лейблов (первая колонка) для строк формы с непустыми значениями */
    updateLabelHighlight(values) {
        if (this.uiType !== FilterUiType.form)
            return;
        // Соберём активные ключи data-key (а не "чистые" key), чтобы точно мапиться к DOM
        const fieldMap = this.collectFieldMap(); // key -> map (содержит dataKey)
        const activeKeys = new Set(values.map((v) => v.key));
        const nodeState = new Map();
        for (const fm of Object.values(fieldMap)) {
            // 2026-04-29: для подсветки берём тот же узел, который вернул collectFieldMap(),
            // чтобы составные виджеты не теряли привязку к своему реальному DOM-источнику.
            const node = fm.sourceElement
                ?? this.host.querySelector(`[data-key="${fm.dataKey}"]`);
            if (!node)
                continue;
            const next = nodeState.get(node) ?? false;
            nodeState.set(node, next || activeKeys.has(fm.key));
        }
        for (const [node, isActive] of nodeState) {
            const labelTd = this._findLabelTdForField(node);
            if (!labelTd)
                continue;
            labelTd.style.fontWeight = isActive ? "bold" : "";
        }
    }
    /** Найти TD-лейбл (первый <td> в той же строке) для узла с data-key */
    _findLabelTdForField(inputEl) {
        const td = inputEl.closest("td");
        const tr = td?.closest("tr");
        if (!td || !tr)
            return null;
        const tds = Array.from(tr.children).filter((c) => c instanceof HTMLTableCellElement);
        if (tds.length === 2) {
            const [labelTd, valueTd] = tds;
            if (valueTd.contains(inputEl))
                return labelTd;
        }
        return null;
    }
    /**
     * Собирает полные записи фильтра (схема + опциональное значение) из DOM.
     * В отличие от collectValues, не теряет поля без значения.
     */
    collectEntries() {
        const fieldMap = this.collectFieldMap();
        const values = this._collectValuesFromFieldMap(fieldMap);
        const byKey = new Map();
        for (const v of values)
            byKey.set(v.key, v);
        const out = [];
        for (const key of Object.keys(fieldMap)) {
            out.push({
                schema: fieldMap[key],
                value: byKey.get(key)
            });
        }
        return out;
    }
    _collectValuesFromFieldMap(fieldMap) {
        // пары data-key → значение
        const dataKeyIndex = new Map();
        for (const [k, fm] of Object.entries(fieldMap))
            dataKeyIndex.set(fm.dataKey, k);
        let raw = encodeValues(this.host);
        const direct = [];
        const widgetNodes = this.host.querySelectorAll("[data-widget]");
        widgetNodes.forEach((node) => {
            const widgets = Widget.allOn(node);
            // 2026-04-29: у составных виджетов сначала забираем готовые записи фильтра,
            // а уже потом добираем карту значений как общий fallback.
            for (const w of widgets) {
                if (!isWithFilterEntries(w))
                    continue;
                const entries = w.getFilterEntries() ?? [];
                for (const entry of entries) {
                    const schema = entry?.schema;
                    const value = entry?.value;
                    if (!schema?.key || !value)
                        continue;
                    const encoded = this._encodeFilterValue(value.value);
                    if (encoded === undefined)
                        continue;
                    direct.push({
                        key: schema.key,
                        value: encoded,
                        valueTitle: value.valueTitle ?? value.label ?? encoded,
                        label: value.label ?? schema.label ?? schema.key
                    });
                    raw[schema.key] = encoded;
                }
            }
            for (const w of widgets) {
                if (!isWithMultipleValues(w))
                    continue;
                const vals = w.getValues();
                if (!vals)
                    continue;
                for (const [k, v] of Object.entries(vals)) {
                    // 2026-04-29: карту значений виджета нормализуем в общий raw-словарь,
                    // чтобы дальше отработали одинаковые правила кодирования и фильтрации.
                    const encoded = this._encodeFilterValue(v);
                    if (encoded === undefined)
                        continue;
                    raw[k] = encoded;
                }
            }
        });
        const out = [];
        for (const [rawKey, rawVal] of Object.entries(raw)) {
            const key = dataKeyIndex.has(rawKey) ? dataKeyIndex.get(rawKey) : this._extractKey(rawKey);
            if (!key)
                continue;
            const fm = fieldMap[key];
            if (!this._isNotNull(rawVal, fm))
                continue;
            const valueStr = String(rawVal);
            const label = fm?.label || key;
            const el = fm?.sourceElement;
            out.push({
                key,
                value: valueStr,
                valueTitle: this._resolveValueTitle(el, valueStr, fm),
                label,
            });
        }
        return out.concat(direct);
    }
    /**
     * Применить внешнее состояние (например, из URL) к DOM.
     * Ставит значения только для известных полей текущего host.
     */
    applyState(stateByKey) {
        if (!stateByKey)
            return;
        const fieldMap = this.collectFieldMap();
        for (const [key, fm] of Object.entries(fieldMap)) {
            if (!Object.prototype.hasOwnProperty.call(stateByKey, key))
                continue;
            const v = stateByKey[key] ?? null;
            // 2026-04-29: внешнее состояние применяем через общий setter,
            // чтобы multi-value виджеты получали свои значения по контракту.
            this._setFieldMapValue(fm, v);
        }
    }
}
/**
* Преобразует строковое значение (например из data-атрибутов) в boolean.
*
* Поддерживает:
*  - "true", "1"  -> true
*  - "false", "0" -> false
*  - undefined / null / "" -> false
*
* @param value Значение для преобразования
*/
function parseBoolean(value) {
    if (value == null)
        return false;
    const normalized = value.toString().trim().toLowerCase();
    return normalized === "true" || normalized === "1";
}

export { FilterDomManager, FilterUiType, parseBoolean };
